import { useEffect, useState } from "react";
import { Steps } from "antd";
import AccountInformation from "./components/AccountInformation";
import FinancialInfo from "./components/FinancialInfo";
import Declaration from "./components/Declaration";
import EKYC from "./components/EKYC";
import ApplicationStatus from "./components/ApplicationStatus";
import Loader from "../../../components/Loader";
import {
  getIndividualAccount,
  updateEkycStatus,
} from "../../../api/request/individualOnboarding";
import { GetAllKycByIdApi } from "../../../api/request/kyc";
import { handleApiError } from "../../../utils/handleApiError";
import toast from "react-hot-toast";

const { Step } = Steps;

const IndividualOnboardingSteps = ({ userId, step, canUpdate = true }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [data, setData] = useState(null);
  const [completedStep, setCompletedStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [isKycPending, setIsKycPending] = useState(false);
  const isReadOnly = isKycPending || !canUpdate;

  const syncAccountProgress = async ({
    showLoader = false,
    preserveCurrentStep = false,
  } = {}) => {
    if (showLoader) setIsLoading(true);
    try {
      const { data } = await getIndividualAccount(
        new URLSearchParams({ userId: userId }).toString()
      );
      if (data?.success) {
        const accountData = data?.data;
        setData(accountData);

        if (!preserveCurrentStep) {
          setCurrentStep(accountData?.currentStep);
          setCompletedStep(accountData?.currentStep - 1);
        } else {
          setCompletedStep((prev) =>
            Math.max(prev, (accountData?.currentStep ?? 1) - 1)
          );
        }
      }
      const { data: kycResponse } = await GetAllKycByIdApi(userId);
      const kycStatus = kycResponse?.data?.kycStatus;
      if (kycStatus === "Pending") setIsKycPending(true);
      if (kycStatus === "Rejected") setIsKycPending(false);
    } catch (error) {
    } finally {
      if (showLoader) setIsLoading(false);
    }
  };

  useEffect(() => {
    if (userId && Number(step) === 11) {
      syncAccountProgress({ showLoader: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, step]);

  const onNext = () => {
    setCurrentStep((prev) => {
      setCompletedStep((completed) => Math.max(completed, prev));
      return prev + 1;
    });
    syncAccountProgress({ preserveCurrentStep: true });
  };
  const handleKYCNext = async () => {
    if (!canUpdate) return;
    try {
      setIsSubmitLoading(true);

      const payload = {
        isKYCCompleted: true,
      };

      const { data } = await updateEkycStatus(payload, userId);

      if (data?.success) {
        toast.success(data?.message || "KYC status updated successfully");
        onNext();
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsSubmitLoading(false);
    }
  };
  const onPrevious = () => setCurrentStep((prev) => prev - 1);

  const handleStepClick = (step) => {
    if (step > completedStep + 1) return;
    setCurrentStep(step);
  };

  const steps = [
    {
      key: 1,
      title: "Account Information",
      content: (
        <AccountInformation
          onNext={onNext}
          onPrevious={onPrevious}
          userId={userId}
          isSubmitted={false}
          isReadOnly={isReadOnly}
          canUpdate={canUpdate}
          id={data?.personalInfoId}
        />
      ),
    },
    {
      key: 2,
      title: "Financial Info",
      content: (
        <FinancialInfo
          onNext={onNext}
          onPrevious={onPrevious}
          userId={userId}
          isSubmitted={false}
          isReadOnly={isReadOnly}
          canUpdate={canUpdate}
          id={data?.employeeFinancialInfoId}
        />
      ),
    },
    {
      key: 3,
      title: "Declaration",
      content: (
        <Declaration
          onNext={onNext}
          onPrevious={onPrevious}
          userId={userId}
          isSubmitted={false}
          isReadOnly={isReadOnly}
          canUpdate={canUpdate}
          // declarationInfo={data?.declarationInfo}
        />
      ),
    },
    {
      key: 4,
      title: "EKYC",
      content: (
        <EKYC
          onNext={canUpdate ? handleKYCNext : onNext}
          onPrevious={onPrevious}
          userId={userId}
          isSubmitted={false}
          isKYCSubmitting={isSubmitLoading}
          canUpdate={canUpdate}
          onKycPendingChange={setIsKycPending}
          id={data?.kycId}
        />
      ),
    },
    {
      key: 5,
      title: "Application Status",
      content: <ApplicationStatus id={data?.applicationStatusId} />,
    },
  ];

  if (isLoading) return <Loader />;

  return (
    <div className="w-full">
      <div className="overflow-x-auto sticky top-0 py-5 bg-white z-10">
        <div className="min-w-[720px] sm:min-w-0">
          <Steps current={currentStep - 1} size="small">
            {steps.map((step) => (
              <Step
                key={step?.key}
                title={step?.title}
                className="cursor-pointer"
                onClick={() => handleStepClick(step?.key)}
              />
            ))}
          </Steps>
        </div>
      </div>

      <div className="min-h-[400px]">{steps[currentStep - 1]?.content}</div>
    </div>
  );
};

export default IndividualOnboardingSteps;
